// Feedback for the internal beta: the β button and sheet (Beta.tsx), the trail of recent steps that rides along
// (lib/trail.ts), page errors (errors.ts) and reports that wait for a connection (send.ts).
//   installErrorReporting()   first thing at boot, so early errors are caught
//   installFeedback()         after the app renders: trail listeners, β layer, shortcut, offline queue
import { appStore } from '../store';
import { routerStore, SCREEN_NAMES } from '../router';
import { onNative, shellInfo } from '../native';
import { crumb } from '../lib/trail';
import { mountBeta, openFeedback } from './Beta';
import { flushQueue } from './send';

export { installErrorReporting, reportError } from './errors';
export { openFeedback, setBetaQuiet } from './Beta';

const TAPPABLE = 'button, a, [role="button"], [role="tab"], [role="switch"], [role="option"], [role="checkbox"], input[type="checkbox"], input[type="radio"], summary, label';

let installed = false;
export function installFeedback(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Taps: the button's label only (never what was typed). Taps inside the β layer are left out.
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element) || t.closest('[data-beta]')) return;
    const el = t.closest(TAPPABLE);
    if (!el) return;
    const label = (el.getAttribute('aria-label') || (el as HTMLElement).innerText || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60);
    crumb(`tap ${label ? `"${label}"` : el.tagName.toLowerCase()}`);
  }, true);

  let screen = routerStore.get().s;
  crumb(`screen ${screen} (${SCREEN_NAMES[screen]})`);
  routerStore.subscribe((r) => { if (r.s !== screen) { screen = r.s; crumb(`screen ${r.s} (${SCREEN_NAMES[r.s]})`); } });

  appStore.subscribe((s, p) => {
    if (s.live !== p.live) crumb(`live ${s.live}`);
    if (s.toast && s.toast !== p.toast) crumb(`toast "${s.toast.text}"`);
    if (s.chat.error && s.chat.error !== p.chat.error) crumb(`chat error "${s.chat.error}"`);
    if (s.chat.limit && !p.chat.limit) crumb('chat free limit reached');
    if (s.chat.crisis && !p.chat.crisis) crumb('chat showed the crisis card');
    if (!!s.token !== !!p.token) crumb(s.token ? 'signed in' : 'signed out');
  });

  onNative((e) => {
    if (e.type === 'push-token') { crumb(`native push-token ${e.platform}`); return; }
    const extra = 'route' in e ? ` ${e.route}` : 'action' in e && e.action ? ` ${e.action}` : '';
    crumb(`native ${e.type}${extra}`);
  });
  window.addEventListener('online', () => { crumb('online'); void flushQueue(); });
  window.addEventListener('offline', () => crumb('offline'));
  document.addEventListener('visibilitychange', () => crumb(`app ${document.visibilityState === 'visible' ? 'in front' : 'in background'}`));

  // Ctrl/Cmd+Shift+B opens the sheet from anywhere (desktop, browsers with a keyboard).
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && (e.key === 'B' || e.key === 'b')) { e.preventDefault(); openFeedback(); }
  });

  void shellInfo();
  mountBeta();
  setTimeout(() => { void flushQueue(); }, 5000);
}
